/**
 * rtc消息监听
 */
import {
  LocalStreamStats,
  MediaType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  RemoteStreamStats,
  StreamRemoveReason,
  DeviceInfo,
  AutoPlayFailedEvent,
  PlayerEvent,
} from '@volcengine/rtc';
import { useDispatch } from 'react-redux';
import { useRef } from 'react';

import {
  IUser,
  remoteUserJoin,
  remoteUserLeave,
  updateLocalUser,
  updateRemoteUser,
  addAutoPlayFail,
  removeAutoPlayFail,
  updateAITalkState,
  setHistoryMsg,
  setCurrentMsg,
  updateMsg,
} from '@/store/slices/room';
import RtcClient, { IEventListener } from './RtcClient';

import { setMicrophoneList, updateSelectedDevice } from '@/store/slices/device';
import config from '@/config';
import { post } from '@/utils/request';

const useRtcListeners = (): IEventListener => {
  const dispatch = useDispatch();
  const playStatus = useRef<{ [key: string]: { audio: boolean; video: boolean } }>({});

  /**
   * 用户加入
   * @param e
   */
  const handleUserJoin = (e: onUserJoinedEvent) => {
    const extraInfo = JSON.parse(e.userInfo.extraInfo || '{}');
    const userId = extraInfo.user_id || e.userInfo.userId;
    const username = extraInfo.user_name || e.userInfo.userId;
    dispatch(
      remoteUserJoin({
        userId,
        username,
      })
    );
  };

  /**
   * 用户离开
   * @param e
   */
  const handleUserLeave = (e: onUserLeaveEvent) => {
    dispatch(remoteUserLeave(e.userInfo));
    dispatch(removeAutoPlayFail(e.userInfo));
  };

  /**
   * 用户发布音频
   * @param e
   */
  const handleUserPublishStream = (e: { userId: string; mediaType: MediaType }) => {
    const { userId } = e;
    const payload: IUser = { userId };
    payload.publishAudio = true;
    dispatch(updateRemoteUser(payload));
  };

  /**
   * 用户停止音频
   * @param e
   */
  const handleUserUnpublishStream = (e: {
    userId: string;
    mediaType: MediaType;
    reason: StreamRemoveReason;
  }) => {
    const { userId, mediaType } = e;

    const payload: IUser = { userId };
    if (mediaType === MediaType.AUDIO) {
      payload.publishAudio = false;
    }

    if (mediaType === MediaType.AUDIO_AND_VIDEO) {
      payload.publishAudio = false;
    }

    dispatch(updateRemoteUser(payload));
  };

  const handleRemoteStreamStats = (e: RemoteStreamStats) => {
    dispatch(
      updateRemoteUser({
        userId: e.userId,
        audioStats: e.audioStats,
      })
    );
  };

  const handleLocalStreamStats = (e: LocalStreamStats) => {
    dispatch(
      updateLocalUser({
        audioStats: e.audioStats,
      })
    );
  };

  /**
   * 音频状态变更
   * @param device
   */
  const handleAudioDeviceStateChanged = async (device: DeviceInfo) => {
    const devices = await RtcClient.getDevices();

    if (device.mediaDeviceInfo.kind === 'audioinput') {
      let deviceId = device.mediaDeviceInfo.deviceId;
      if (device.deviceState === 'inactive') {
        deviceId = devices.audioInputs?.[0].deviceId || '';
      }
      RtcClient.switchDevice('microphone', deviceId);
      dispatch(setMicrophoneList(devices.audioInputs));

      dispatch(
        updateSelectedDevice({
          selectedMicrophone: deviceId,
        })
      );
    }
  };

  /**
   * 自动播放失败
   * @param event
   */
  const handleAutoPlayFail = (event: AutoPlayFailedEvent) => {
    const { userId, kind } = event;
    let playUser = playStatus.current?.[userId] || {};
    playUser = { ...playUser, [kind]: false };
    playStatus.current[userId] = playUser;

    dispatch(
      addAutoPlayFail({
        userId,
      })
    );
  };

  const addFailUser = (userId: string) => {
    dispatch(addAutoPlayFail({ userId }));
  };

  const playerFail = (params: { type: 'audio' | 'video'; userId: string }) => {
    const { type, userId } = params;
    let playUser = playStatus.current?.[userId] || {};

    playUser = { ...playUser, [type]: false };

    const { audio, video } = playUser;

    if (audio === false || video === false) {
      addFailUser(userId);
    }

    return playUser;
  };

  const handlePlayerEvent = (event: PlayerEvent) => {
    const { userId, rawEvent, type } = event;
    let playUser = playStatus.current?.[userId] || {};

    if (!playStatus.current) return;

    if (rawEvent.type === 'playing') {
      playUser = { ...playUser, [type]: true };
      const { audio, video } = playUser;
      if (audio !== false && video !== false) {
        dispatch(removeAutoPlayFail({ userId }));
      }
    } else if (rawEvent.type === 'pause') {
      playUser = playerFail({ type, userId });
    }

    playStatus.current[userId] = playUser;
  };

  const handleUserStartAudioCapture = (_: { userId: string }) => {
    dispatch(updateAITalkState({ isAITalking: true }));
  };

  const handleUserStopAudioCapture = (_: { userId: string }) => {
    dispatch(updateAITalkState({ isAITalking: false }));
  };

  /**
   * 房间收到消息
   * @param event
   */
  const handleRoomBinaryMessageReceived = (event: { userId: string; message: ArrayBuffer }) => {
    const { message } = event;
    const decoder = new TextDecoder('utf-8');
    const str = decoder.decode(message);
    const start = str.indexOf('{');
    const context = JSON.parse(str.substring(start, str.length)) || {};
    const data = context.data?.[0] || {};

    if (data) {
      const { text: msg, definite, userId: user, paragraph } = data;
      if ((window as any)._debug_mode) {
        dispatch(setHistoryMsg({ msg, user, paragraph, definite }));
      } else {
        const isAudioEnable = RtcClient.getAudioBotEnabled();
        if (isAudioEnable) {
          dispatch(setHistoryMsg({ text: msg, user, paragraph, definite }));
        }
      }
      dispatch(setCurrentMsg({ msg, definite, user, paragraph }));

      //完整的段落并且说话的是用户
      if(paragraph && user !== config.BotName){
        //关闭用户的麦克风
        console.log("关闭用户的麦克风",msg)
        RtcClient.setAudioVolume(0);
        //3s后自动开启
        setTimeout(()=>{
          console.log("定时开启用户的麦克风")
          RtcClient.setAudioVolume(100);
        },3000)

        //保存会话消息
        let conversationId = localStorage.getItem("conversationId");
        post("/cbc/conversation/ops/message/generatePunc",{
          conversationId: conversationId,
          content: msg,
        }).then((res)=>{
          //处理返回
          if(res.code === 200){
            //替换消息
            let message = res.data;
            const match = message.match(/^[^\u3002\uff0c\uff1b\uff1a\uff01\uff1f\uff08\uff09\u3001\uff0e\u300a\u300b]+/);
            if (match) {
              console.log("第一个标点符号前面的文字：",match);
            }
            //前五条历史消息
            dispatch(updateMsg({
              originMsg:message,
              matchMsg: match
            }))
          }
        })
      }else{
        //开启麦克风
        //console.log("开启麦克风",msg)
        RtcClient.setAudioVolume(100);
      }

    }
  };

  return {
    handleUserJoin,
    handleUserLeave,
    handleUserPublishStream,
    handleUserUnpublishStream,
    handleRemoteStreamStats,
    handleLocalStreamStats,
    handleAudioDeviceStateChanged,
    handleAutoPlayFail,
    handlePlayerEvent,
    handleUserStartAudioCapture,
    handleUserStopAudioCapture,
    handleRoomBinaryMessageReceived,
  };
};

export default useRtcListeners;
